/**
 * Project map coordinates into the Giro3D Instance's render frame.
 *
 * The Instance is created in the dataset's `displayCrs` when the converter
 * supplies one (e.g. `EPSG:32636` for Turkey), and only falls back to Web
 * Mercator for legacy scenes. Camera positioning code that hard-codes the
 * Mercator formulas therefore aims at coordinates from the wrong projection —
 * for a UTM frame that is millions of metres away, so the scene renders black
 * with the data far off-screen.
 *
 * Web Mercator keeps its closed-form spherical formulas rather than going
 * through proj4, so existing EPSG:3857 scenes are numerically unchanged.
 */

import proj4 from 'proj4';

const WEB_MERCATOR_HALF_CIRCUMFERENCE = 20037508.342789244;

export interface FramePoint {
  readonly x: number;
  readonly y: number;
}

export function isWebMercatorFrame(frameCrs: string | null | undefined): boolean {
  const code = (frameCrs ?? '').toUpperCase();
  return code === '' || code === 'EPSG:3857' || code === 'EPSG:900913';
}

export function lonLatToWebMercator(lon: number, lat: number): FramePoint | null {
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
  // Clamp off the poles: tan(π/4 + φ/2) diverges at ±90°.
  const clampedLat = Math.min(85.051129, Math.max(-85.051129, lat));
  const rad = (clampedLat * Math.PI) / 180;
  const x = (lon / 180) * WEB_MERCATOR_HALF_CIRCUMFERENCE;
  const y =
    (Math.log(Math.tan(Math.PI / 4 + rad / 2)) / Math.PI)
    * WEB_MERCATOR_HALF_CIRCUMFERENCE;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y };
}

/**
 * A UTM zone code is formulaic, so a missing proj4 def can be synthesised
 * rather than failing the camera move. Covers `EPSG:326xx` (north) and
 * `EPSG:327xx` (south), which is what the converter emits for most datasets.
 */
export function utmProj4Def(frameCrs: string): string | null {
  const match = /^EPSG:(326|327)(\d{2})$/.exec((frameCrs ?? '').toUpperCase());
  if (!match) return null;
  const zone = Number(match[2]);
  if (!Number.isInteger(zone) || zone < 1 || zone > 60) return null;
  const south = match[1] === '327' ? ' +south' : '';
  return `+proj=utm +zone=${zone}${south} +datum=WGS84 +units=m +no_defs`;
}

const registered = new Set<string>();

/** Make `frameCrs` known to proj4. Returns false when no def can be resolved. */
export function ensureFrameProj4Def(
  frameCrs: string,
  frameProj4?: string | null,
): boolean {
  const code = (frameCrs ?? '').trim().toUpperCase();
  if (!code) return false;
  if (registered.has(code)) return true;
  try {
    if (proj4.defs(code)) {
      registered.add(code);
      return true;
    }
  } catch {
    /* proj4 throws for unknown codes in some versions — fall through */
  }
  const def = (frameProj4 && frameProj4.trim().length > 0 ? frameProj4 : null)
    ?? utmProj4Def(code);
  if (!def) return false;
  try {
    proj4.defs(code, def);
    registered.add(code);
    return true;
  } catch (err) {
    console.warn(`[frameProjection] could not register ${code} with proj4`, err);
    return false;
  }
}

/** Project a coordinate from `sourceCrs` into the render frame. */
export function projectToFrame(
  x: number,
  y: number,
  sourceCrs: string,
  frameCrs: string | null | undefined,
  frameProj4?: string | null,
): FramePoint | null {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  const source = (sourceCrs ?? '').toUpperCase();
  const frame = (frameCrs ?? '').toUpperCase();
  const sourceIsDegrees = source === 'EPSG:4326' || source === 'OGC:CRS84';

  if (isWebMercatorFrame(frame)) {
    if (sourceIsDegrees) return lonLatToWebMercator(x, y);
    if (isWebMercatorFrame(source)) return { x, y };
  }
  if (source === frame) return { x, y };

  const sourceCode = sourceIsDegrees ? 'EPSG:4326' : source;
  if (!ensureFrameProj4Def(frame, frameProj4)) return null;
  if (!sourceIsDegrees && !ensureFrameProj4Def(sourceCode)) return null;
  try {
    const [fx, fy] = proj4(sourceCode, frame, [x, y]) as [number, number];
    if (!Number.isFinite(fx) || !Number.isFinite(fy)) return null;
    return { x: fx, y: fy };
  } catch (err) {
    console.warn(
      `[frameProjection] ${sourceCode} → ${frame} transform failed`,
      err,
    );
    return null;
  }
}
